import { useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Bandeau, Champ, Modale } from './ui';
import { dateFr } from '../lib/format';
import { T, langue, reglagesRegionaux } from '../i18n';
import { useAuth } from '../state/auth';
import type { ReleveKm as Releve, Vehicule } from '../api/types';

/**
 * Relever un compteur, pour n'importe quelle voiture.
 *
 * Le kilometrage ne se saisissait que dans un etat des lieux — donc uniquement
 * au depart et au retour d'une location. Or on regarde le compteur en faisant
 * le plein, au retour du garage, ou simplement en passant devant la voiture. Et
 * c'est ce chiffre-la qui declenche la vidange.
 *
 * Un compteur ne recule jamais : une valeur inferieure est presque toujours une
 * faute de frappe. Le serveur la conserve pour la trace mais ne l'applique pas,
 * et on le dit ici plutot que de laisser croire que c'est enregistre.
 */
export function ReleveKm({ vehicules, vehiculeInitial, onFini, onFermer }: {
  vehicules: Vehicule[];
  vehiculeInitial?: string;
  onFini: (message: string) => void;
  onFermer: () => void;
}) {
  const [vehicle, setVehicle] = useState(vehiculeInitial ?? '');
  const [km, setKm] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [historique, setHistorique] = useState<Releve[]>([]);
  const { peut } = useAuth();
  const L = T.releveKm;
  const u = reglagesRegionaux().uniteDistance;
  const nb = (x: number) => x.toLocaleString(langue === 'fr' ? 'fr-FR' : 'en-US');

  /*
   * Les fenetres d'inventaire, annoncees AVANT la saisie.
   *
   * « Sabrina ne peut modifier les km que 2 fois par mois, du 1er au 3 et du 15
   * au 18 » — quatre jours a chaque fois, pour qu'il y ait toujours un jour
   * ouvre. Le dire apres coup, en refus, ferait taper un chiffre pour rien.
   */
  const jour = new Date().getDate();
  const libre = peut('modifier.vehicule');
  const dansFenetre = (jour >= 1 && jour <= 3) || (jour >= 15 && jour <= 18);
  const bloque = !libre && !dansFenetre;

  const choisi = vehicules.find((v) => v._id === vehicle);

  useEffect(() => {
    if (!vehicle) { setHistorique([]); return; }
    api.get<Releve[]>(`/operations/kilometrages?vehicle=${vehicle}`)
      .then((l) => setHistorique(l.slice(0, 5))).catch(() => setHistorique([]));
  }, [vehicle]);

  const saisi = Number(km);
  const recule = Boolean(choisi) && km !== '' && saisi < (choisi?.km ?? 0);
  const ecart = choisi && km !== '' ? saisi - choisi.km : 0;

  async function envoyer() {
    setErreur(''); setEnvoi(true);
    try {
      const r = await api.post<{ recul: boolean; kmPrecedent: number; vehicule: Vehicule }>(
        '/operations/kilometrages',
        { vehicle, km: saisi, commentaire: commentaire || undefined },
      );
      onFini(r.recul
        ? L.conserveNonApplique(nb(saisi), nb(r.kmPrecedent), u)
        : L.applique(r.vehicule.immatriculation, nb(r.kmPrecedent), nb(saisi), u));
    } catch (e) {
      setErreur(e instanceof ApiError ? e.erreur : L.erreur);
    } finally { setEnvoi(false); }
  }

  return (
    <Modale titre={L.titre} onFermer={onFermer}>
      {!libre && (
        <div style={{ marginBottom: 12 }}>
          <Bandeau type={bloque ? 'ko' : 'info'}
                   titre={bloque ? L.horsPeriode : L.periodeOuverte}>
            {L.seRelevent}<b>{L.du1au3a}<sup>{L.er}</sup>{L.du1au3b}</b>{L.et}<b>{L.du15au18}</b>{L.chaqueMois}
            {bloque
              ? <>{L.bloque(jour)}</>
              : <>{L.ouvert(jour)}</>}
          </Bandeau>
        </div>
      )}

      <Champ label={L.vehicule}>
        <select value={vehicle} onChange={(e) => { setVehicle(e.target.value); setKm(''); }}>
          <option value="">{L.choisir}</option>
          {vehicules.map((v) => (
            <option key={v._id} value={v._id}>{v.marque} {v.modele} · {v.immatriculation}</option>
          ))}
        </select>
      </Champ>

      {choisi && (
        <div style={{ marginTop: 12 }}>
          <Champ label={L.compteur}
                 aide={L.dernierConnu(nb(choisi.km), u)}>
            <input type="number" inputMode="numeric" value={km} autoFocus
                   onChange={(e) => setKm(e.target.value)} placeholder={String(choisi.km)} />
          </Champ>
          {km !== '' && !recule && ecart > 0 && (
            <p className="mini sourdine" style={{ marginTop: 6 }}>
              {L.depuis(nb(ecart), u)}
            </p>
          )}
        </div>
      )}

      {recule && (
        <div style={{ marginTop: 12 }}>
          <Bandeau type="ko" titre={L.inferieurTitre}>
            {L.inferieurA}<b>{L.pas}</b>{L.inferieurB}
          </Bandeau>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <Champ label={L.commentaire} aide={L.commentaireAide}>
          <input value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
        </Champ>
      </div>

      {historique.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div className="micro">{L.derniers}</div>
          <table className="tbl compact" style={{ marginTop: 6 }}>
            <tbody>
              {historique.map((h) => (
                <tr key={h._id}>
                  <td className="mini sourdine">{dateFr(h.date)}</td>
                  <td className="num">{nb(h.km)} {u}</td>
                  <td className="mini sourdine">{L.sources[h.source] ?? h.source}</td>
                  <td className="mini sourdine">{h.creePar?.nom ?? ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}

      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>{L.annuler}</button>
        <button className="btn btn-p" disabled={bloque || !vehicle || km === '' || saisi < 0 || envoi}
                onClick={() => void envoyer()}>
          {envoi ? L.enregistrement : L.enregistrer}

        </button>
      </div>
    </Modale>
  );
}
